/**
 * dealCounters — the shared counting module for the Deals Filters surface
 * (T55.12). Pure + portable (CP1: no platform imports).
 *
 * T55 § Design invariants (2026-07-23 amendment) locks the contract this
 * module exists to satisfy: the count unit is the rendered deal card, and
 * there are exactly three number kinds —
 *   - deal count   — the heading "· N deals", the drawer's "Show N deals",
 *     and the rendered card list all read `dealCount` (the SAME number, by
 *     construction — every one of them derives from `filteredRowIds`).
 *   - option count — each filter row's count (`categoryOptions` /
 *     `attributeOptions` / `storeOptions`), DISJUNCTIVE: the OTHER filter
 *     sections are applied, the counted section's own selection is
 *     omitted; sections AND together, options within a section OR.
 *   - tab count    — the number of *selectable* options in a tab. No
 *     function needed for this: it is `categoryOptions.length` /
 *     `attributeOptions.length` / `storeOptions.length` at the call site.
 *
 * Runs client-side over the already-fetched rows (zero-request narrowing,
 * invariant 3.10) — every option count recomputes live as selections
 * change, which a one-shot server computation over the pre-selection result
 * set cannot do.
 *
 * A row's `id` is the STABLE identity of one rendered deal card (a
 * store-scoped card key in browse — a flyer deal shown at two stores is two
 * cards — or the deal id in search, which is already deduplicated across
 * stores server-side). Rows sharing an `id` collapse to the first
 * occurrence before anything else runs, so a combo deal (one card, several
 * categories from `dealTagPayload`'s per-line union) — or any accidental
 * duplicate row — contributes at most 1 to any count, while every
 * category/attribute/store it carries stays visible in the respective
 * option lists.
 *
 * Categories AND Attributes are both exhaustive (T55.14 joins Attributes to
 * the T55.12 Category treatment): unlike the retired server-side derivation
 * (`backend/src/services/searchRefinementChips.ts`'s `PER_SECTION_CAP = 4`,
 * left untouched on the backend and no longer consumed by the drawer's
 * Attribute tab), this module never excludes a category already typed into
 * the search bar, never caps either list, and never treats "every row
 * carries it" as a reason to withhold an option — the caller renders every
 * returned option as a normal row (both Categories and Attributes: the
 * shared tri-state row control, T55.17).
 *
 * `categoryOptions`' and `attributeOptions`' counts are both PRESENCE-only —
 * a row counts toward a term the instant its weight is `> 0` — a different,
 * looser threshold than the MATCHING rules below (`matchesCategories` /
 * `matchesAttributes`, used only to narrow `filteredRowIds`): an include
 * (category) / require (attribute) also reads `weight > 0`, but an exclude
 * (either kind) reads `weight >= EXCLUDE_CONFIDENCE_FLOOR` (0.5). So a
 * below-floor category or attribute still shows a nonzero option count even
 * though excluding it wouldn't drop every row that count includes. The
 * disjunctive base for each option list omits the WHOLE section it belongs
 * to (every term's selection in that section, not just the counted term's)
 * — so an option's count never moves as ANY option in its OWN section
 * cycles off → include/require → exclude (T55.17 AC3).
 *
 * T55.17 — Categories join Attributes' off → include/require → exclude
 * cycle (T55 § Design invariants 2026-07-23 amendment item 6): a product
 * legitimately carries several same-level categories, so "not X" cannot be
 * expressed by include-picks alone. `categories` on `DealCounterRow` is now
 * a confidence-weighted map exactly like `attributes` (the wire payload
 * already carries a `weight` per category tag — see `matchesCategories`'
 * doc comment below for the verify-point finding), and category excludes
 * reuse the identical `EXCLUDE_CONFIDENCE_FLOOR` attribute excludes use.
 * Includes stay OR within the Category section (alternatives); excludes are
 * AND-NOT in both sections.
 */

export interface DealCounterRow {
  /** Stable identity of the rendered deal card — the dedup key. */
  id: string;
  /**
   * Lowercased category name → confidence weight (0/absent = not carried).
   * Mirrors `attributes` (T55.17) — a combo deal may carry several.
   */
  categories: ReadonlyMap<string, number>;
  /** Lowercased attribute name → confidence weight (0/absent = not carried). */
  attributes: ReadonlyMap<string, number>;
  /** StoreLocation ids this row is on offer at (a search row may span several). */
  storeIds: ReadonlyArray<string>;
}

/** Categories cycle off → include → exclude — includes are OR (alternatives), not AND. */
export type DealCounterCategoryIntent = 'include' | 'exclude';
export type DealCounterAttributeIntent = 'require' | 'exclude';

export interface DealCounterSelections {
  categories: ReadonlyMap<string, DealCounterCategoryIntent>;
  attributes: ReadonlyMap<string, DealCounterAttributeIntent>;
  stores: ReadonlySet<string>;
}

export interface DealCounterOption {
  term: string;
  count: number;
}

export interface DealCounterResult {
  /** ids of the rows matching every active selection. */
  filteredRowIds: ReadonlySet<string>;
  /** filteredRowIds.size — the ONE number the heading, "Show N deals", and the rendered card list all share. */
  dealCount: number;
  /** Every category present (weight > 0) across the (attribute + store)-filtered rows, disjunctive w.r.t. the WHOLE category section (T55.17). */
  categoryOptions: DealCounterOption[];
  /** Every attribute present (weight > 0) across the (category + store)-filtered rows, disjunctive w.r.t. the WHOLE attribute section (T55.14). */
  attributeOptions: DealCounterOption[];
  /** Every store present across the (category + attribute)-filtered rows, disjunctive w.r.t. the store selection itself. */
  storeOptions: DealCounterOption[];
}

/**
 * T55 § Design invariants item 6 (2026-07-23 amendment) — category AND
 * attribute excludes reuse this SAME 0.5 confidence floor.
 *
 * Verify-point finding (T55.17): the floor is applied HERE, module-side —
 * not a server payload concept. The wire payload for both tag kinds already
 * carries a per-tag `weight` (`category_tags[].weight` /
 * `attribute_tags[].weight`, both populated by the same backend
 * `dealTagPayload` helper, `backend/src/routes/flyerDeals.ts`, and by the
 * search read's `category_tags`/`attribute_tags` schema,
 * `packages/shared/src/types/FlyerDeals.ts`) — so there is no payload gap to
 * close. `matchesAttributes` already applied this floor to attribute
 * excludes only; `matchesCategories` below reuses the identical constant so
 * category excludes apply the exact same threshold to the exact same kind
 * of already-shipped weight data.
 */
const EXCLUDE_CONFIDENCE_FLOOR = 0.5;

function dedupeById(rows: readonly DealCounterRow[]): DealCounterRow[] {
  const seen = new Set<string>();
  const deduped: DealCounterRow[] = [];
  for (const row of rows) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    deduped.push(row);
  }
  return deduped;
}

/**
 * Categories are OR within the section (any ONE included category is
 * enough — alternatives, not constraints) and AND-NOT for excludes, mirror-
 * ing `matchesAttributes` below except for that OR/AND asymmetry (T55 §
 * Design invariants item 6: "includes stay OR for categories... AND for
 * attributes"). See the `EXCLUDE_CONFIDENCE_FLOOR` doc comment for the
 * verify-point finding on why the exclude arm can reuse the same floor.
 */
function matchesCategories(
  row: DealCounterRow,
  categories: DealCounterSelections['categories'],
): boolean {
  const includes: string[] = [];
  const excludes: string[] = [];
  for (const [term, intent] of categories) {
    if (intent === 'include') includes.push(term);
    else excludes.push(term);
  }
  if (includes.length > 0 && !includes.some((term) => (row.categories.get(term) ?? 0) > 0)) {
    return false;
  }
  for (const term of excludes) {
    if ((row.categories.get(term) ?? 0) >= EXCLUDE_CONFIDENCE_FLOOR) return false;
  }
  return true;
}

function matchesAttributes(
  row: DealCounterRow,
  attributes: DealCounterSelections['attributes'],
): boolean {
  for (const [term, intent] of attributes) {
    const weight = row.attributes.get(term) ?? 0;
    if (intent === 'require' && weight <= 0) return false;
    if (intent === 'exclude' && weight >= EXCLUDE_CONFIDENCE_FLOOR) return false;
  }
  return true;
}

function matchesStores(row: DealCounterRow, stores: ReadonlySet<string>): boolean {
  return stores.size === 0 || row.storeIds.some((id) => stores.has(id));
}

/**
 * The `categoryOptions`/`attributeOptions` presence threshold (weight > 0) —
 * deliberately looser than the MATCHING thresholds above, since an option's
 * count means "carries this term at all", not "would survive
 * including/requiring/excluding it".
 */
function presenceTerms(weights: ReadonlyMap<string, number>): ReadonlyArray<string> {
  const terms: string[] = [];
  for (const [term, weight] of weights) {
    if (weight > 0) terms.push(term);
  }
  return terms;
}

function countDistinctBy(
  rows: readonly DealCounterRow[],
  termsOf: (row: DealCounterRow) => ReadonlyArray<string>,
): DealCounterOption[] {
  const counts = new Map<string, number>();
  for (const row of rows) {
    for (const term of new Set(termsOf(row))) {
      counts.set(term, (counts.get(term) ?? 0) + 1);
    }
  }
  return Array.from(counts.entries())
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => b.count - a.count || a.term.localeCompare(b.term));
}

/**
 * (rows, selections) → filtered rows + disjunctive per-option deal counts.
 * See the file header for the full contract.
 */
export function computeDealCounters(
  rows: readonly DealCounterRow[],
  selections: DealCounterSelections,
): DealCounterResult {
  const deduped = dedupeById(rows);

  const filteredRowIds = new Set(
    deduped
      .filter(
        (row) =>
          matchesCategories(row, selections.categories) &&
          matchesAttributes(row, selections.attributes) &&
          matchesStores(row, selections.stores),
      )
      .map((row) => row.id),
  );

  // Disjunctive: each section counts over the OTHER sections applied and its
  // OWN selection omitted, so checking one option never zeroes out its
  // siblings.
  const categoryBase = deduped.filter(
    (row) => matchesAttributes(row, selections.attributes) && matchesStores(row, selections.stores),
  );
  const attributeBase = deduped.filter(
    (row) => matchesCategories(row, selections.categories) && matchesStores(row, selections.stores),
  );
  const storeBase = deduped.filter(
    (row) => matchesAttributes(row, selections.attributes) && matchesCategories(row, selections.categories),
  );

  return {
    filteredRowIds,
    dealCount: filteredRowIds.size,
    categoryOptions: countDistinctBy(categoryBase, (row) => presenceTerms(row.categories)),
    attributeOptions: countDistinctBy(attributeBase, (row) => presenceTerms(row.attributes)),
    storeOptions: countDistinctBy(storeBase, (row) => row.storeIds),
  };
}
